import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { TranslateService } from '@ngx-translate/core';
import { HotelFilterForm } from '@pages/travel/hotel/data-models';
import { PaginationService } from '@pages/travel/hotel/hotel-search-results/hotel-paginator/hotel-paginator.service';
import { CustomisableVariables } from '@shared/models/customisable-variables';
import { ActivityFilter } from '@shared/models/filter.model';
import { HotelSearchPayload } from '@shared/models/hotel-search-payload.model';
import {
  HotelResult,
  HotelRoom,
  HotelRoomPriceOrdering,
  HotelSearchResponse,
} from '@shared/models/hotel-search-response.model';
import { MappedHotelSearchResult } from '@shared/models/search/mapped-hotel-search-result';
import { CustomisableVariablesService } from '@shared/services/customisable-variables.service';
import { DataLayerUtilsService } from '@shared/services/data-layer-utils.service';
import { SiteSettingsService } from '@shared/services/site-settings.service';
import { ApiEndpoint } from '@shared/shared/enums/api-endpoints.enum';
import {
  IProductGroupOrdering,
  ISiteSettings,
  SortBy,
  SortDirection,
  SortType,
} from '@shared/shared/interfaces/site-settings.interfaces';
import { CardRibbon } from '@shared/shared/modules/card/models/card';
import { clearLocalSelectedRooms } from '@shared/utils/travel-utils';
import {
  AMENITIES_EXPANDED,
  BOOKING_HOTEL_ADYEN_INSTALMENT_ENABLED,
  HOTEL_FILTER_FORM,
  HOTEL_SEARCH_RESULTS,
  SELECTED_HOTEL_LOCAL_STORAGE_KEY,
  SELECTED_HOTEL_MAIN_IMAGE,
  SELECTED_ROOM_LOCAL_STORAGE_KEY,
  TRAVEL_HOTEL_SEARCH_LOCAL_STORAGE_KEY,
  TRAVEL_SEARCH_TOGGLES_LOCAL_STORAGE_KEY,
} from 'app.config';
import * as localForage from 'localforage';
import { forkJoin, of } from 'rxjs';
import { catchError, filter, map, mergeMap, switchMap, takeUntil, withLatestFrom } from 'rxjs/operators';
import * as fromHotelRoomActions from '../actions/hotel-room.actions';
import * as fromActions from '../actions/hotel-search.actions';

@Injectable()
export class HotelSearchEffects {
  constructor(
    private actions$: Actions,
    private http: HttpClient,
    private siteSettingsService: SiteSettingsService,
    private customVarService: CustomisableVariablesService,
    private translateService: TranslateService,
    private paginationService: PaginationService
  ) {}

  public restoreHotels$ = createEffect(() =>
    this.actions$.pipe(
      ofType(fromActions.RESTORE_HOTELS),
      switchMap(() => forkJoin([localForage.getItem(HOTEL_SEARCH_RESULTS)])),
      map(([hotelSearchResultsString]: [string]) => {
        const hotelSearchString = localStorage.getItem(TRAVEL_HOTEL_SEARCH_LOCAL_STORAGE_KEY);
        const selectedHotelString = localStorage.getItem(SELECTED_HOTEL_LOCAL_STORAGE_KEY);
        const selectedRoomString = localStorage.getItem(SELECTED_ROOM_LOCAL_STORAGE_KEY);

        return new fromActions.RestoreHotelsSuccess({
          hotelSearch: hotelSearchString ? JSON.parse(hotelSearchString) : null,
          selectedHotel: selectedHotelString ? JSON.parse(selectedHotelString) : null,
          selectedRoom: selectedRoomString ? JSON.parse(selectedRoomString) : null,
          hotelSearchResults: hotelSearchResultsString ? JSON.parse(hotelSearchResultsString) : null,
        });
      })
    )
  );

  public createHotelSearch$ = createEffect(() =>
    this.actions$.pipe(
      ofType(fromActions.CREATE_HOTEL_SEARCH),
      map((action: fromActions.CreateHotelSearch) => {
        this.saveToLocalStorage(action.payload);
        return new fromHotelRoomActions.ClearHotelRoomResults();
      })
    )
  );

  public selectRoom$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(fromActions.SELECT_HOTEL_ROOM),
        map((action: fromActions.SelectHotelRoom) =>
          localStorage.setItem(SELECTED_ROOM_LOCAL_STORAGE_KEY, JSON.stringify(action.payload))
        )
      ),
    { dispatch: false }
  );

  public getHotelSearchResults$ = createEffect(() =>
    this.actions$.pipe(
      ofType(fromActions.GET_HOTEL_SEARCH__RESULTS),
      withLatestFrom(this.siteSettingsService.fetchSiteSettings(), this.customVarService.variables),
      //delay(2000),
      switchMap(
        ([action, settings, customVars]: [fromActions.GetHotelSearchResults, ISiteSettings, CustomisableVariables]) => {
          //return of(hotelSearchMockedResults).pipe(
          return this.http.post(ApiEndpoint.TRAVEL_HOTEL_SEARCH, action.payload).pipe(
            takeUntil(this.actions$.pipe(ofType(fromActions.CHECK_HOTEL_SEARCH_CANCEL))),
            map((hotelSearchResponse: HotelSearchResponse) => {
              localForage.setItem(HOTEL_SEARCH_RESULTS, JSON.stringify(hotelSearchResponse));
              localStorage.setItem(HOTEL_SEARCH_RESULTS, JSON.stringify(hotelSearchResponse.search_reference_id));

              const isPricePerNight = settings.switches.travel_hotels_rooms_price_per_night;
              const numberOfRooms = action.payload.room_requests.length;

              hotelSearchResponse.travel_hotels_rooms_price_per_night =
                settings.switches.travel_hotels_rooms_price_per_night;
              hotelSearchResponse.room_requests = action.payload.room_requests.length;

              if (settings.switches.hotel_search_results_redesign) {
                let res = JSON.parse(JSON.stringify(hotelSearchResponse.results));
                res = res.filter((hotel) => +hotel.rating >= 3);

                const productGroupOrdering: IProductGroupOrdering[] = settings.product_group_ordering;
                const helper = this.getProductCodeOrderNumbers(productGroupOrdering);

                const sortedHotels = this.sortHotels(res, settings.hotel_ordering_level, helper) || [];
                const travelPreferences = settings.switches.preferences_travel;
                const pwpSwitch = settings.switches.enable_pay_with_points && settings.switches.enable_pwp_hotels_path;
                const adyenSwitch = settings.switches.enable_adyen_instalments;
                const isMultiComponentBooking = JSON.parse(
                  localStorage.getItem(TRAVEL_SEARCH_TOGGLES_LOCAL_STORAGE_KEY)
                ).multi;

                const mappedSortedResults = sortedHotels?.map((hotel) =>
                  this.mapResultToHotelCard(
                    hotel,
                    productGroupOrdering,
                    isPricePerNight,
                    numberOfRooms,
                    customVars,
                    travelPreferences,
                    pwpSwitch,
                    adyenSwitch,
                    isMultiComponentBooking
                  )
                );

                hotelSearchResponse.mappedSortedHotels = mappedSortedResults;

                const defaultFilters = this.setDefaultHotelFilters(mappedSortedResults);
                hotelSearchResponse.defaultFilters = defaultFilters;
              }

              return new fromActions.GetHotelSearchResultsSuccess(hotelSearchResponse);
            })
          );
        }
      ),
      catchError((err) => {
        return of(new fromActions.GetHotelSearchResultsFailure(DataLayerUtilsService.extractValidationErrors(err)));
      })
    )
  );

  public updateDefaultHotelFilterForm$ = createEffect(() =>
    this.actions$.pipe(
      ofType(fromActions.GET_HOTEL_SEARCH__RESULTS_SUCCESS),
      withLatestFrom(this.siteSettingsService.fetchSiteSettings()),
      filter(
        ([, settings]: [fromActions.GetHotelSearchResultsSuccess, ISiteSettings]) =>
          settings.switches.hotel_search_results_redesign
      ),
      switchMap(([action]: [fromActions.GetHotelSearchResultsSuccess, ISiteSettings]) => {
        return of(new fromActions.UpdateDefaultHotelFilterForm(action.payload.defaultFilters));
      })
    )
  );

  public restoreHotelFilterForm$ = createEffect(() =>
    this.actions$.pipe(
      ofType(fromActions.GET_HOTEL_SEARCH__RESULTS_SUCCESS),
      withLatestFrom(this.siteSettingsService.fetchSiteSettings()),
      filter(
        ([, settings]: [fromActions.GetHotelSearchResultsSuccess, ISiteSettings]) =>
          settings.switches.hotel_search_results_redesign
      ),
      mergeMap(([action]: [fromActions.GetHotelSearchResultsSuccess, ISiteSettings]) => {
        const localStoredHotelFilters = this.restoreHotelFilters();
        const hotelFilters = localStoredHotelFilters || action.payload.defaultFilters;
        return of(new fromActions.UpdateFilterForm(hotelFilters));
      })
    )
  );

  public updateFilterForm$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(fromActions.UPDATE_FILTER_FORM),
        map((action: fromActions.UpdateFilterForm) => {
          const hotelDetails = JSON.parse(JSON.stringify(action.payload));
          this.paginationService.setCurrentPage(1);
          hotelDetails['paging'] = this.paginationService.getCurrentPage();

          localStorage.setItem(HOTEL_FILTER_FORM, JSON.stringify(hotelDetails));
        })
      ),
    { dispatch: false }
  );

  public clearFilterForm$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(fromActions.CLEAR_FILTER_FORM),
        map(() => {
          localStorage.removeItem(HOTEL_FILTER_FORM);
          localStorage.removeItem(AMENITIES_EXPANDED);
        })
      ),
    { dispatch: false }
  );

  private setSortPriorityForHotels(hotelResults: HotelResult[], productCodeOrderNumbers: any): HotelResult[] {
    if (!hotelResults || !hotelResults.length) return;

    return hotelResults.map((hotel: HotelResult) => ({
      ...hotel,
      sort_priority: Math.min(
        ...hotel.rooms.map((room) => productCodeOrderNumbers[room.product_code] || Number.MAX_SAFE_INTEGER)
      ),
    }));
  }

  private getProductCodeOrderNumbers(productGroupOrdering: IProductGroupOrdering[]) {
    const productCodeOrderNumbers = {};
    productGroupOrdering.forEach((element) => {
      const pCodes = element.product_code.split(',');
      pCodes.forEach((pCode) => (productCodeOrderNumbers[pCode] = element.priority));
    });
    return productCodeOrderNumbers;
  }

  private sortHotels(
    hotelResults: HotelResult[],
    hotelOrderingLevel: any,
    productCodeOrderNumbers: any
  ): HotelResult[] {
    if (!hotelResults || !hotelResults.length) return;
    let hotels = [...hotelResults];

    hotels = this.setSortPriorityForHotels(hotels, productCodeOrderNumbers);

    hotels.sort((a, b) => a.sort_priority - b.sort_priority || this.sortingHotelsNextLevel(a, b, hotelOrderingLevel));
    return hotels;
  }

  private sortingHotelsNextLevel(a: HotelResult, b: HotelResult, hotelOrderingLevel: any): number {
    if (!a || !b) return 0;

    if (!hotelOrderingLevel || Object.keys(hotelOrderingLevel).length === 0) {
      return (
        parseFloat(b.rating) - parseFloat(a.rating) || parseFloat(a.from_price.amount) - parseFloat(b.from_price.amount)
      );
    }

    const level = hotelOrderingLevel;

    const params = { a, b };

    const second_level_sort_a_b = this.getLevelSort(
      params,
      level.second_level_sort_type,
      level.second_level_sort_by,
      SortDirection.AB
    );

    const second_level_sort_b_a = this.getLevelSort(
      params,
      level.second_level_sort_type,
      level.second_level_sort_by,
      SortDirection.BA
    );

    const second_level_sort = second_level_sort_a_b - second_level_sort_b_a;

    const third_level_sort_a_b = this.getLevelSort(
      params,
      level.third_level_sort_type,
      level.third_level_sort_by,
      SortDirection.AB
    );

    const third_level_sort_b_a = this.getLevelSort(
      params,
      level.third_level_sort_type,
      level.third_level_sort_by,
      SortDirection.BA
    );

    const third_level_sort = third_level_sort_a_b - third_level_sort_b_a;

    return second_level_sort || third_level_sort;
  }

  private getLevelSort(
    source: Record<string, HotelResult>,
    sort_type: SortType,
    sort_by: SortBy,
    sort_direction: SortDirection
  ): number {
    let sortDirection;

    switch (sort_direction) {
      case SortDirection.AB:
        sortDirection = sort_type === SortType.ASC ? 'a' : 'b';
        break;
      case SortDirection.BA:
        sortDirection = sort_type === SortType.ASC ? 'b' : 'a';
        break;
      default:
        break;
    }

    const str =
      sort_by === SortBy.PRICE && source[sortDirection]['from_price']
        ? source[sortDirection]['from_price']['amount']
        : source[sortDirection]['rating'];

    return parseFloat(str);
  }

  public getHotelRecommendedResults$ = createEffect(() =>
    this.actions$.pipe(
      ofType(fromActions.GET_HOTEL_RECOMMENDED__RESULTS),
      switchMap((_action: fromActions.GetHotelRecomendedResults) => {
        return this.http.post(ApiEndpoint.TRAVEL_HOTEL_RECOMMENDED, _action.payload).pipe(
          takeUntil(this.actions$.pipe(ofType(fromActions.CHECK_HOTEL_SEARCH_CANCEL))),
          map((data: HotelSearchResponse) => {
            return new fromActions.GetHotelRecomendedResultsSuccess(data);
          }),
          catchError((err) => {
            return of(
              new fromActions.GetHotelRecomendedResultsFailure(DataLayerUtilsService.extractValidationErrors(err))
            );
          })
        );
      })
    )
  );

  public selectHotelResult$ = createEffect(() =>
    this.actions$.pipe(
      ofType(fromActions.SELECT_HOTEL_RESULT),
      withLatestFrom(this.siteSettingsService.fetchSiteSettings()),
      switchMap(([action, settings]: [fromActions.SelectHotelResult, ISiteSettings]) =>
        of(
          new fromActions.SelectHotelResultSuccess({
            hotelId: action.payload,
            productGroupsForBanner: settings.product_group_ordering,
            productGroups: settings.hotel_room_ordering_level.product_group,
            isPricePerRoomPerNight: settings.switches.travel_hotels_rooms_price_per_night,
            isProductGroupOrdering: !!settings.hotel_room_ordering_level?.product_group?.length,
            defaultPriceOrder:
              settings.hotel_room_ordering_level?.sort_by_price_ascending !== false
                ? HotelRoomPriceOrdering.ASCENDING
                : HotelRoomPriceOrdering.DESCENDING,
          })
        )
      )
    )
  );

  public clearHotelSearch$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(fromActions.CLEAR_HOTEL_SEARCH),
        map(() => this.clearLocalStorage())
      ),
    { dispatch: false }
  );

  public clearSelectedHotel$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(fromActions.CLEAR_SELECTED_HOTEL),
        map(() => this.clearLocalStorage())
      ),
    { dispatch: false }
  );

  public saveToLocalStorage(data: HotelSearchPayload) {
    localStorage.setItem(TRAVEL_HOTEL_SEARCH_LOCAL_STORAGE_KEY, JSON.stringify(data));
  }

  public clearLocalStorage() {
    localStorage.removeItem(TRAVEL_HOTEL_SEARCH_LOCAL_STORAGE_KEY);
    localStorage.removeItem(SELECTED_HOTEL_LOCAL_STORAGE_KEY);
    localStorage.removeItem(SELECTED_ROOM_LOCAL_STORAGE_KEY);
    localForage.removeItem(HOTEL_SEARCH_RESULTS);
    localStorage.removeItem(HOTEL_SEARCH_RESULTS);
    localStorage.removeItem(SELECTED_HOTEL_MAIN_IMAGE);
    localStorage.removeItem(HOTEL_FILTER_FORM);
    localStorage.removeItem(AMENITIES_EXPANDED);
    localStorage.removeItem(BOOKING_HOTEL_ADYEN_INSTALMENT_ENABLED);
    clearLocalSelectedRooms();
  }

  private mapResultToHotelCard(
    result: HotelResult,
    productGroupOrdering: IProductGroupOrdering[],
    isPricePerNight: boolean,
    numberOfRooms: number,
    customVars: CustomisableVariables,
    travelPreferences: boolean,
    pwpSwitch: boolean,
    adyenSwitch: boolean,
    isMultiComponentBooking: boolean
  ): MappedHotelSearchResult {
    if (!result) console.error('mapResultToHotelCard with undefined result param');

    const fromPrice = isPricePerNight ? +result.from_price.amount : +result.rooms[0].total_price.amount * numberOfRooms;
    const ribbonData = this.createBanner(result, productGroupOrdering, customVars);

    const pwpEnabled = pwpSwitch && result.rooms.some((room: HotelRoom) => !room.pay_local_required);
    const isBenefitsHotel = result.rooms.some(
      (room: HotelRoom) =>
        !!productGroupOrdering.find(
          (o) => !!room.product_code && o.product_code.includes(room.product_code) && o.product_code !== 'INS'
        )
    );

    const adyenInstalmentsAvailable = this.checkAdyenInstalmentsAvailability(
      result.rooms,
      adyenSwitch,
      isMultiComponentBooking
    );

    return {
      productId: result.product_id,
      attributeIds: result.attribute_ids,
      preferred: result.is_preferred,
      instalmentPaymentsAvailable: result.instalment_payments_available,
      adyenInstalmentsAvailable,
      instalmentBannerText: undefined,
      imageUrl: result.image?.source,
      fromPrice: fromPrice,
      currencyCode: result.from_price.currency_code,
      payWithPointsEnabled: pwpEnabled,
      backupImageUrl: '',
      hotelLocationName: result.sub_title,
      hotelName: result.title,
      rating: parseFloat(result.rating),
      travelPreferences: travelPreferences,
      ribbonData,
      location: result.location,
      isBenefitsHotel,
    } as MappedHotelSearchResult;
  }

  private checkAdyenInstalmentsAvailability(
    rooms: HotelRoom[],
    adyenSwitch: boolean,
    isMultiComponentBooking: boolean
  ): boolean {
    if (!adyenSwitch || isMultiComponentBooking) {
      return false;
    }

    const hotelSearch = JSON.parse(localStorage.getItem(TRAVEL_HOTEL_SEARCH_LOCAL_STORAGE_KEY));
    const numberOfRooms = hotelSearch?.room_requests?.length;

    if (numberOfRooms === 1) {
      const groupedRooms = rooms.reduce((acc, room) => {
        const key = `${room.meal_basis}-${room.room_type}-${room.is_non_refundable}`;
        if (!acc[key]) {
          acc[key] = [];
        }
        acc[key].push(room);
        return acc;
      }, {});

      for (const key in groupedRooms) {
        const roomGroup = groupedRooms[key];
        if (roomGroup.length > 1) {
          roomGroup.sort((a, b) => a.total_price.amount - b.total_price.amount);
        }
        const cheapestRoom = roomGroup[0];
        if (!cheapestRoom.pay_local_required && cheapestRoom.adyen_instalments_allowed && cheapestRoom.seq === 1) {
          return true;
        }
      }
    } else {
      return rooms.some((room) => room.adyen_instalments_allowed && !room.pay_local_required && room.seq === 1);
    }

    return false;
  }

  public createBanner(
    hotel: HotelResult,
    productGroupOrdering: IProductGroupOrdering[],
    customVars: CustomisableVariables
  ): CardRibbon {
    const productCodesOfRooms = Array.from(new Set(hotel.rooms.map((room: HotelRoom) => room.product_code)))?.filter(
      (c: string) => !!c
    );
    const productGroupsOfRooms = productGroupOrdering.filter(
      (pg) => !!productCodesOfRooms.find((c) => pg.product_code.includes(c))
    );
    const productGroupsOfRoomsNotInst = productGroupsOfRooms.filter(
      (pg: IProductGroupOrdering) => !['Installments', 'Instalments'].includes(pg.product_group)
    );
    productGroupsOfRoomsNotInst.sort((a, b) => a.priority - b.priority);

    if (!productGroupsOfRoomsNotInst.length) {
      return null;
    }

    const bannerKey = productGroupsOfRoomsNotInst[0].banner.key;
    const productGroup = productGroupsOfRoomsNotInst[0].product_group;
    const cardRibbon: CardRibbon = {
      filled: true,
      text: customVars[bannerKey],
      productGroup,
    };

    return cardRibbon;
  }

  private setDefaultHotelFilters(mappedSortedHotels: MappedHotelSearchResult[]): HotelFilterForm {
    const priceArray = mappedSortedHotels.map((hotel) => hotel.fromPrice);
    const amenityIdsInSearchResults = Array.from(
      new Set(mappedSortedHotels.map((hotel) => hotel.attributeIds).flat())
    ).sort((a, b) => a - b);
    const amenitiesInSearchResults = amenityIdsInSearchResults.map((id) => {
      return { value: id, selected: false, enabled: true } as ActivityFilter;
    });
    const benefitsHotelExists = mappedSortedHotels.some((hotel) => hotel.isBenefitsHotel);
    const benefitsFilterName = this.translateService.instant('SideFilterComponent:TitleHotelCollection');
    const adyenInstalmentHotelExists = mappedSortedHotels.some((hotel) => hotel.adyenInstalmentsAvailable);
    const adyenInstalmentFilterName = this.translateService.instant('SideFilterComponent:TitleInstalments');

    const popularFilters = [];
    if (benefitsHotelExists) {
      popularFilters.push({ name: benefitsFilterName, selected: false, enabled: true, popularFilterType: 'benefits' });
    }
    if (adyenInstalmentHotelExists) {
      popularFilters.push({
        name: adyenInstalmentFilterName,
        selected: false,
        enabled: true,
        popularFilterType: 'adyenInstalments',
      });
    }

    const defaultFilterForm = {
      hotelName: '',
      priceRange: [Math.min(...priceArray), Math.max(...priceArray), ''],
      starRating: [
        {
          value: 5,
          selected: false,
          enabled: mappedSortedHotels.some((hotel) => Math.floor(hotel.rating) === 5),
          name: 'Star rating 5',
        },
        {
          value: 4,
          selected: false,
          enabled: mappedSortedHotels.some((hotel) => Math.floor(hotel.rating) === 4),
          name: 'Star rating 4',
        },
        {
          value: 3,
          selected: false,
          enabled: mappedSortedHotels.some((hotel) => Math.floor(hotel.rating) === 3),
          name: 'Star rating 3',
        },
      ],
      amenities: amenitiesInSearchResults,
      popularFilters,
    } as HotelFilterForm;

    return defaultFilterForm;
  }

  private restoreHotelFilters(): HotelFilterForm {
    const localFilter = localStorage.getItem(HOTEL_FILTER_FORM);
    if (!localFilter) {
      return null;
    }
    return JSON.parse(localFilter);
  }
}
